import {
  Stack,
  Table,
  Button,
  Flex,
  Text,
  Box,
  Container,
} from '@mantine/core';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import {
  IconArrowLeft,
  IconNotebook,
  IconSparkles,
} from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { Page, ProcessHeader } from '../../components';
import { formatNumberWithCommas } from '@/helpers/string';
import classes from './style.module.css';
import { chartColors, loanGoalOptions } from '@/helpers/enums';
import { calculateMixTotals, calculatePayments } from '../CustomizedMix/functions';

const CustomizedMixSummary = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const mixes = useSelector((state) => state.mixes.mixes);

  const handleReturn = () => {
    navigate(-1);
  };

  const tableHeaders = [
    'סכום',
    'מסלול',
    'מטרה',
    'תקופה',
    'ריבית',
    'מדד',
    'החזר חודשי',
    'החזר כולל',
  ];

  // Create chart data for all mixes (each mix is one line)
  const createChartData = () => {
    const chartData = {};

    mixes.forEach((mix, mixIndex) => {
      const mixTotals = calculateMixTotals(mix);
      if (mixTotals.allSchedules && mixTotals.allSchedules.length > 0) {
        const maxMonths = Math.max(...mixTotals.allSchedules.map((schedule) => schedule.length));

        for (let month = 1; month <= maxMonths; month += 1) {
          if (!chartData[month]) {
            chartData[month] = { month };
          }

          let monthTotal = 0;
          mixTotals.allSchedules.forEach((schedule) => {
            const monthData = schedule.find((item) => item.month === month);
            if (monthData) {
              monthTotal += parseFloat(monthData.payment);
            }
          });

          if (monthTotal > 0) {
            chartData[month][`תמהיל ${mixIndex + 1}`] = parseFloat(monthTotal.toFixed(0));
          }
        }
      }
    });

    return Object.values(chartData).sort((a, b) => a.month - b.month);
  };

  const chartData = createChartData();

  // Export to PDF function
  const exportToPDF = async () => {
    const element = document.getElementById('pdf-content');
    if (!element) return;

    try {
      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        logging: false,
      });

      const imgData = canvas.toDataURL('image/png');
      // eslint-disable-next-line new-cap
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4',
      });

      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = pageWidth - 20;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      const yPosition = 10;

      pdf.addImage(imgData, 'PNG', 10, yPosition, imgWidth, imgHeight);

      // Handle multiple pages if needed
      let heightLeft = imgHeight - (pageHeight - 30);
      let position = 0;

      while (heightLeft > 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 10, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      pdf.save('סיכום-התמהילים.pdf');
    } catch (error) {
      // Handle error (e.g., show notification)
    }
  };

  const totalTemplate = ({
    icon,
    title,
    content,
    actions,
  }) => (
    <Page.Section>
      <Flex justify='space-between' align='center' mb='md'>
        <Flex align='center' gap='sm'>
          {icon}
          <Text fw={'bold'}>{title}</Text>
        </Flex>
        {actions && <Flex gap='sm'>{actions}</Flex>}
      </Flex>
      <Box className={classes.boxTemplate} bg={'white'}>
        {content}
      </Box>
    </Page.Section>
  );

  const DataRow = (obj) => {
    const {
      label, value, bold, color,
    } = obj;

    return (
      <Flex justify='space-between' align='center' >
        <Text size='sm' color='gray.6'>
          {label}
        </Text>
        <Text size='sm' fw={bold ? 700 : 400} color={color || 'black'}>
          {value}
        </Text>
      </Flex>
    );
  };

  const renderMortgageMixSummaryTable = (mix, index) => (
    <Table
      striped
      highlightOnHover
      className={classes['mortgage-table']}
    >
      <Table.Thead>
        <Table.Tr>
          {tableHeaders.map((header) => (
            <Table.Th key={header} c='#64748B'>
              {header}
            </Table.Th>
          ))}
        </Table.Tr>
      </Table.Thead>

      <Table.Tbody>
        {mix.tranches.map((tranche, trancheIndex) => {
          const {
            amount, interest, period, linkage,
          } = tranche;

          const calculatePaymentsObj = {
            amount,
            interestRate: interest,
            periodMonths: period,
            linkage,
          };

          const { monthlyPayment, totalRepayment } = calculatePayments(calculatePaymentsObj);
          const loanGoal = loanGoalOptions.find((g) => g.value === tranche.goal);

          return (
            <Table.Tr key={`${index}-${trancheIndex}`} bg='white'>
              <Table.Td>{formatNumberWithCommas(tranche.amount)}</Table.Td>
              <Table.Td>{tranche.tranche}</Table.Td>
              <Table.Td>{loanGoal.label}</Table.Td>
              <Table.Td> {`${tranche.period} חודשים`} </Table.Td>
              <Table.Td>{tranche.interest ? `${tranche.interest}%` : '-'} </Table.Td>
              <Table.Td> {tranche.linkage ? `${tranche.linkage}%` : '-'}  </Table.Td>
              <Table.Td> {formatNumberWithCommas(monthlyPayment.toFixed(0))}</Table.Td>
              <Table.Td> {formatNumberWithCommas(totalRepayment)}</Table.Td>
            </Table.Tr>
          );
        })}
      </Table.Tbody>
    </Table>
  );

  const summeryMixesSection = totalTemplate({
    title: t('pages.customizedMixSummary.summaryMixes.title'),
    icon: <IconNotebook color='#7C3AED' />,
    actions: [<Button
      key='export-button'
      onClick={exportToPDF}
      size='sm'
      rightSection={<IconArrowLeft />}
    > {t('pages.customizedMixSummary.exportButton.title')} </Button>],
    content: mixes.map((mix, index) => (
      <Box key={index} p={'md'}>
        <Flex align='center' gap='sm'>
          <Text fw='bold' fz='sm'>  תמהיל {index + 1}</Text>
          <Box className={classes.summeryMixestitleLine} />
        </Flex>

        {renderMortgageMixSummaryTable(mix, index)}
      </Box>
    )),
  });

  const compareMixesSection = totalTemplate({
    title: t('pages.customizedMixSummary.comparedMixes.title'),
    icon: <IconNotebook size={20} color='#7C3AED' />,
    content: <Flex >
      {mixes.map((mix, index) => {
        const {
          totalLoanAmount,
          totalMonthlyPayment,
          maxMonthlyPayment,
          totalInterestPayment,
          totalLinkagePayment,
          totalRepayment,
          repaymentPerShekel,
        } = calculateMixTotals(mix);

        return (
          <Box key={index} p='xl' className={classes.compareBox}>
            <Text fw={700} size='lg' className={classes.compareBoxTitle}>סיכום תמהיל {index + 1}</Text>
            <Stack spacing='xs' mt={'md'}>
              <DataRow label={t('pages.customizedMixSummary.compareMixesSection.totalAmount')} value={formatNumberWithCommas(totalLoanAmount)} bold />
              <DataRow label={t('pages.customizedMixSummary.compareMixesSection.totalMonthlyPayment')} value={formatNumberWithCommas(totalMonthlyPayment)} color='#3B82F6' bold />
              <DataRow label={t('pages.customizedMixSummary.compareMixesSection.maxMonthlyPayment')} value={formatNumberWithCommas(maxMonthlyPayment)} />
              <DataRow label={t('pages.customizedMixSummary.compareMixesSection.totalInterestPayment')} value={formatNumberWithCommas(totalInterestPayment)} />
              <DataRow label={t('pages.customizedMixSummary.compareMixesSection.totalLinkagePayment')} value={formatNumberWithCommas(totalLinkagePayment)} />
              <Box bg='#F0F7FF' p='md' my='sm' className={classes.totalRepaymentBox}>
                <DataRow
                  label={t('pages.customizedMixSummary.compareMixesSection.totalRepayment')}
                  value={formatNumberWithCommas(totalRepayment?.toLocaleString())}
                  color='#3B82F6'
                  bold
                />
              </Box>

              <DataRow label={t('pages.customizedMixSummary.compareMixesSection.repaymentPerShekel')} value={formatNumberWithCommas(repaymentPerShekel)} bold />
            </Stack>
          </Box>
        );
      })}
    </Flex>,
  });

  const graphSection = totalTemplate({
    title: t('pages.customizedMixSummary.graphSection.title'),
    icon: <IconNotebook size={20} color='#7C3AED' />,
    content: chartData.length > 0 && (
    <div className={classes.graphSection}>
      <ResponsiveContainer width='100%' height='100%'>
        <LineChart
          data={chartData}
          margin={{
            top: 20, right: 30, left: 60, bottom: 20,
          }}
        >
          <defs>
            {mixes.map((_, index) => (
              <linearGradient key={`grad-${index}`} id={`colorMix${index}`} x1='0' y1='0' x2='0' y2='1'>
                <stop offset='5%' stopColor={chartColors[index % chartColors.length]} stopOpacity={0.1}/>
                <stop offset='95%' stopColor={chartColors[index % chartColors.length]} stopOpacity={0}/>
              </linearGradient>
            ))}
          </defs>

          <CartesianGrid strokeDasharray='3 3' vertical={false} stroke='#F1F5F9' />

          <XAxis
            dataKey='month'
            axisLine={false}
            tickLine={false}
            tick={{ fill: '#94A3B8', fontSize: 12 }}
            dy={20}
            padding={{ left: 40 }}
          />

          <YAxis
            axisLine={false}
            tickLine={false}
            width={80}
              tick={{
                textAnchor: 'end', fill: '#94A3B8', fontSize: 12,
              }}
            tickFormatter={(value) => formatNumberWithCommas(value)}
            domain={[0, 'auto']}
          />

          <Tooltip
            contentStyle={{
              borderRadius: '8px',
              border: 'none',
              boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
              direction: 'rtl',
              textAlign: 'right',
            }}
            formatter={(value) => [formatNumberWithCommas(value), 'החזר חודשי']}
            labelFormatter={(label) => `חודש: ${label}`}
          />

          <Legend
            verticalAlign='top'
            align='right'
            iconType='circle'
            content={({ payload }) => (
              <Flex justify='flex-end' gap='md' mb='xl'>
                {payload.map((entry, index) => (
                  <Flex key={`item-${index}`} align='center' gap={4}>
                    <Box style={{ backgroundColor: entry.color }} className={classes.graphLabel} />
                    <Text size='xs' fw={500} c='gray.7'>{entry.value}</Text>
                  </Flex>
                ))}
              </Flex>
            )}
          />

          {mixes.map((mix, index) => (
            <Line
              key={`תמהיל ${index + 1}`}
              type='monotone'
              dataKey={`תמהיל ${index + 1}`}
              stroke={chartColors[index % chartColors.length]}
              strokeWidth={3}
              dot={false}
              activeDot={{ r: 6, strokeWidth: 0 }}
              isAnimationActive={true}
              animationDuration={1500}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
    ),
  });

  return (
    <Page
      title={'מחשבון משכנתא'}
      hideNews={true}
      hideHelp={true}
    >
      <Container size='md'>
        <ProcessHeader
          headerText={t('pages.customizedMixSummary.headerText')}
          subtitle={t('pages.customizedMixSummary.subtitle')}
          headerColor='#F6EBFF'
          headerTextColor='#9F1FF4'
          icon={IconSparkles}
          showReturnButton={true}
          onReturn={handleReturn}
        >

          <Stack gap={24}>
            <div id='pdf-content'>
              {summeryMixesSection}
              {compareMixesSection}
              {graphSection}
            </div>

          </Stack>
        </ProcessHeader>
      </Container>

    </Page>
  );
};

export default CustomizedMixSummary;
